// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useMemo, useState } from 'react';
import { Routes, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { QuestRoute } from '../../_common/api/QuestRoutes';
import { Regex } from '../../_common/api/regex';
import Button from '../../_common/form/Button';
import Form from '../../_common/form/Form';
import Input from '../../_common/form/Input';
import Select from '../../_common/form/Select';
import Title from '../../_common/form/Title';
import { useGlobalState } from '../../_common/globalState/GlobalStateProvider';
import Loading from '../../_common/Loading';
import Modal from '../../_common/Modal';
import Card from '../Card';
import CardInset from '../CardInset';
import ItemList from '../ItemList';
import WashClub from '../WashClub';
import ForceLocation from '../ForceLocation';
import { HamQuest } from '../../../App';
import Survey from './Survey';
import UpdateVehicle from './UpdateVehicle';
import HamLink from '../../_common/form/HamLink';
import MenuRoutes from '../MenuRoutes';
import { currency} from '../../_common/api/utility';
import Cart from '../../_common/Cart';
import Icon from '../../_common/form/Icon';
import { validMethods } from 'workbox-routing/utils/constants';


const PlateImageLink = styled.span`
    color: ${({ theme }) => theme.active};
    padding: 0.2rem 0rem;
    text-decoration: none;
    font-size: 1.05rem;
`
const LeaveButton = styled(Button)`
    background: ${({ theme }) => theme.error};
    font-size: 0.8em;
`
const LeaveButtonModal = styled(Button)`
    background: ${({ theme }) => theme.error};
`
const ItemsWrapper = styled.div`
    height: inherit;
    overflow-x: ${({totalGroups}) => totalGroups > 1 ? 'overlay' : 'none'};
    height: 100%;  
`
const Items = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    height: calc(100% - 3.5em);
    width: fit-content;
    min-width: 100%;
`
const GroupCard = styled.div`
    display: flex;    
    flex-direction: column;
    width: ${({totalGroups}) => totalGroups > 1 ? '70vw' : '75vw'};
    font-size: 0.95em;
    margin-bottom: 0.5em;
    margin-left: ${({index}) => index === 0 ? 0 : '0.5rem' };
    padding: 0.2em 0.5em;
    background-color: ${({ theme }) => theme.mobile.body};
    border-radius: 0.5rem;
    border: 2px solid ${({ theme }) => theme.mobile.tertiary};    
`
const Row = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`
const Span = styled.span`
    text-align: center;
`

const WashClubs = () => {

    const { state: { active_user, site_config }, getWashClub } = useGlobalState();

    const routes = MenuRoutes();

    const { search } = useLocation();
    const query = new URLSearchParams(search);

    const [plans, setPlans] = useState([]);
    const [showingPlans, setShowingPlans] = useState(query.get('forced') ? true : active_user.washClub ? active_user.washClub.status.toLowerCase() === 'inactive' : true);

    // Temporary flag to disable surveys.
    const displaySurvey = true;

    const [washClubStatus, setWashClubStatus] = useState( active_user.washClub ? active_user.washClub.status.toLowerCase() : 'inactive');

    const wcAccountType = active_user.washClub?.accountType?.toLowerCase() ?? 'standard';
    const wcSuspended = washClubStatus === 'suspended';
    const statusMetadata = active_user.washClub?.statusMetadata ?? [];

    const requiredVehicleFields = ["make", "model", "year", "color", "license", "state"];
    const forceVehicleUpdate = !active_user.vehicle || requiredVehicleFields.some(field => !active_user.vehicle?.[field]);
    
    const groupedViolations = useMemo(() => {
        return statusMetadata.violations?.reduce((g, v) =>{
            let key = `${v.plateReadPlate}|${v.violationType}`;
            g[key] = g[key] || [];
            g[key].push(v);
            return g;
        }, {}) ?? {};
    }, [statusMetadata.violations]);

    useEffect(() => {
        setWashClubStatus(active_user.washClub?.status?.toLowerCase() ?? 'inactive');
    }, [active_user.washClub?.status]); 

    useEffect(() => {
        HamQuest.get(QuestRoute.products.washClubs)
            .then((res) => setPlans(res))
            .catch(ex => {
                Modal.Show({
                    type: 'error',
                    title: 'Unable to get Clubs',
                    body: 'Sorry, something went wrong. Please try again.'
                })
            })
    }, [])

    const updateWashClubStatus = async () => {
        await getWashClub();
    }

    const cancelPlan = () => {
        Modal.Confirmation({
            title: active_user.washClub.requiredMonths && active_user.washClub.requiredMonths > 0 ? 'Required Months' : 'Hold On!',
            type: 'warning',
            body: active_user.washClub.requiredMonths && active_user.washClub.requiredMonths > 0 ? 
            `You have ${active_user.washClub.requiredMonths} months remaining in your contract. You will be charged a ${currency.format(active_user.washClub.requiredMonths * active_user.washClub.billingAmount)} fee. Do you still want to cancel?` :
            `Are you sure you want to cancel your Wash Club?`,
            hideClose: true,
            closeAfter: true,
        }).then((res) => {
            if (!res) return;
            Modal.Confirmation({
                title: 'LAST CHANCE!',
                type: 'error',
                body: `Are you sure you want to cancel your Wash Club? You will be able to use the club until the next billing date.`,
                hideClose: true,
                closeAfter: true,
            }).then(res => {
            if (!res) return;
            return displaySurvey ? 
                Modal.Show({
                    title: 'Survey',
                    body: <Survey onSuccess={() => {
                        Modal.Close();
                        return true;
                    }} />,
                    buttons: [],
                    hideClose: true,
                    closeAfter: true,
                }) 
                : res;
        }).then(res => {
            if (!res) return;
            HamQuest.delete(QuestRoute.user.washClub)
                .then((res) => {
                    updateWashClubStatus();
                    Modal.Show({
                        type: 'success',
                        title: 'Wash Club Canceled',
                        body: 'You can continue to use your wash club until your next billing date.'
                    })
                })
                .catch((ex) => {
                    Modal.Show({
                        type: 'error',
                        title: 'Failed to Cancel',
                        body: 'Sorry, something went wrong. Please try again.'
                    })
                })
            })
        });
    }

    const cancelPlanWcRestricted = () => {
        Modal.Show({
            title: 'Leave Club?',
            icon: '',
            type: 'warning',
            body: (
                <>
                    <p>
                        This action will immediately deactivate your wash club account and end recurring wash club charges to your card on file.
                        However, you will no longer have club access through the remainder of the current billing period.
                    </p>
                </>
            ),
            hideClose: true,
            closeAfter: true,             
            buttons: [
                <LeaveButtonModal onClick={() =>{
                    Modal.Promise({
                        title: 'Deactivating account, please wait...',
                        body: <Loading />
                    })
                    .then(() => Modal.Show({
                        type: 'success',
                        title: 'Wash Club Canceled',
                        body: `A confirmation email has been sent to ${active_user.account.email}. Re-subscribe to club access at any time...`                     
                    }))
                    .catch((er) => Modal.Show({
                        type: 'error',
                        title: 'Error',
                        body: `An error occurred deactivation wash club account. Please wait a few minutes and try again.`                        
                    }))
                }}>Leave Club</LeaveButtonModal>,
                <Button onClick={Modal.Close}>Cancel</Button>
            ],         
        });
    }

    const reactivatePlan = () => {
        Modal.Confirmation({
            title: 'Activate Washclub.',
            type: 'warning',
            body: `Are you sure you want reactivate your Wash Club?`,
            hideClose: true,
            closeAfter: true,
        }).then(res => {
            if (!res) return;
            HamQuest.put(QuestRoute.user.washClub)
                .then((res) => {
                    updateWashClubStatus();
                    Modal.Show({
                        type: 'success',
                        title: 'Wash reactivated',
                        body: 'Your wash club has been reactivated.'
                    })
                })
                .catch((ex) => {
                    Modal.Show({
                        type: 'error',
                        title: 'Failed to Reactivate',
                        body: 'Sorry, something went wrong. Please try again.'
                    })
                })
            HamQuest.put(QuestRoute.user.washClub)
                .then((res) => {
                    updateWashClubStatus();
                    Modal.Show({
                        type: 'success',
                        title: 'Wash reactivated',
                        body: 'Your wash club has been reactivated.'
                    })
                })
                .catch((ex) => {
                    Modal.Show({
                        type: 'error',
                        title: 'Failed to Reactivate',
                        body: 'Sorry, something went wrong. Please try again.'
                    })
                })
        });
    }

    const addAdditionalVehicle = (key) => {
        const violations = groupedViolations[key];
        const [plate, violationType] = key.split('|');
        
        Modal.Show({
            title: 'Add Additional Vehicle',
            icon: 'sideCar',
            body: (
                <>
                    <Row>
                        Add this vehicle to your club account and we'll prorate the price to your next billing date.
                        Afterwards, the normal 'Additional Vehicle' club pricing will be charged as part of your regular billing cycle.
                    </Row>
                    <p>
                        <div>ULTIMATE MONTHLY CLUB</div>
                        <div>Additional Vehicle: +$39.99</div>
                    </p>
                    <p>
                        <div>New Monthly Rate: $90.99</div>
                        <div>Next Billing Date: {new Date(active_user.washClub.nextBillingDate).toLocaleDateString()}</div>
                        
                    </p>
                    <p>
                        <b>Charged Today:</b> {currency.format(9.15)}
                    </p>
                </>
            ),
            hideClose: true,
            closeAfter: true,            
            buttons: [
                <Button onClick={() => {
                    Modal.Show({
                        title: 'Vehicle Info',
                        body: (
                            <Form
                                inputs={{
                                    make: '',
                                    model: '',
                                    year: '',
                                    license: plate ?? '',
                                    color: '',
                                    state: '',
                                }}
                                onSubmit={e =>
                                    Modal.Promise({
                                        title: 'Saving Vehicle',
                                        body: <Loading />,
                                        callback: () => {} // HamQuest.post(QuestRoute.user.vehicle, e)
                                    })
                                        .then(() => {
                                            Modal.Close();
                                        })
                                }
                            >
                                <Select
                                    name='state'
                                    label='State'
                                    required
                                    options={
                                        [
                                            {
                                                value: 0,
                                                label: '-'
                                            },
                                            ...site_config.states
                                        ]
                                    }
                                />
                                <Input
                                    name='make'
                                    label='Make'
                                    maxLength={20}
                                    required
                                />
                                <Input
                                    name='model'
                                    label='Model'
                                    maxLength={20}
                                    required
                                />
                                <Input
                                    name='color'
                                    label='Color'
                                    maxLength={20}
                                    required
                                />
                                <Input
                                    name='year'
                                    label='Year'
                                    sanitize={Regex.match.notNumbers}
                                    numpad
                                    maxLength={4}
                                    required
                                />
                                <Input
                                    name='license'
                                    label='License Plate'
                                    required
                                    disabled
                                    maxLength={10}
                                    sanitize={Regex.match.specialChar}
                                    validate={/(\w|\d)+/g}
                                    mask={(e) => e.toUpperCase()}
                                    tag='Used for License Plate Reading'
                                />
                            </Form>
                        )
                    })                    
                }}>Continue</Button>,
                <Button onClick={Modal.Close}>Cancel</Button>
            ]
        });       
    }

    const payforVendedWash = (key) => {
        
        const violations = groupedViolations[key];
        const totalPrice = violations.reduce((a, b) => a + b.transItemPrice, 0);
        
        const items = violations.map(v => v.transItemId);

        Modal.Show({
            title: 'Pay for Wash',
            icon: 'creditCard',
            body: (
                <>
                    <p>
                        We'll apply a one-time charge to your card on file for the original price of the vended wash item(s).
                        If no other violations remain, club access will be immediately restored and regular club billing will continue as normal.
                    </p>
                    <p>
                        <div>
                            {violations.map(v => {
                                return(
                                    <div>
                                        {v.transItemDescription.toUpperCase()}&nbsp;
                                        # {v.transRecordId}:&nbsp;
                                        {currency.format(v.transItemPrice)}
                                    </div>
                                )
                            })}
                            <p>
                                <b>Charged Today:</b> {currency.format(totalPrice)} + Tax
                            </p>
                        </div>
                    </p>                 
                </>
            ),
            hideClose: true,
            closeAfter: true,            
            buttons: [
                <Button onClick={() => HamQuest.post(QuestRoute.purchase.clubSharedWash, items)}>
                    Pay For Wash{violations.length > 1 ? 'es' : ''}
                </Button>,
                <Button onClick={Modal.Close}>Cancel</Button>
            ]
        }).then((res) => {
            if (!res) return;
            Modal.Show({
                title: 'Process Payment',
                body: (
                    <Span>Price and payment review</Span>
                ),
                hideClose: false
            })          
        });
    }

    const showPlateReadImage = (key, index) => {
        const violation = groupedViolations[key][index];

        Modal.Show({
            title: 'Vehicle Image',
            icon: 'camera',
            body: (
                <img width="100%" height="100%" src={violation?.plateReadFullImage} />
            ),
            hideClose: false
        })        
    }

    const WcSuspended = () => {      
        return (
            <Card title='Wash Club Locked' icon='lock'>
                <p>
                    Due to one or more violations of our <HamLink href={routes.termsAndConditions.route}><u>TOS</u></HamLink>,
                    your wash club access is temporarily suspended. Please review the details below for options to restore club access.
                </p>
                <div style={{ height: '100%', minWidth: '100%', overflow: 'hidden' }} data-draggable={true}>
                    <ItemsWrapper totalGroups={Object.entries(groupedViolations).length}>
                        <Items>
                            {Object.entries(groupedViolations)?.map(([key, items], index) => {
                                const [plate, violationType] = key.split('|');
                                const hideAddVehicle = items.some(i => i.plateReadCustomerId ?? false);
                                switch(violationType)
                                {
                                    case 'ClubShared':
                                        return(                                       
                                            <GroupCard totalGroups={Object.entries(groupedViolations).length} index={index}>
                                                <div><b>PLATE #:</b> {plate}</div> 
                                                <div>
                                                    <b>Reason:</b> {violationType}
                                                </div>
                                                <p>
                                                    {items.map((violation, index) => {
                                                            return(
                                                            <CardInset key={violation.transItemId} title={'Date: ' + new Date(violation.transDateTime).toLocaleString()} collapsible>
                                                                <div><b>Vended Item:</b> {violation.transItemDescription.toUpperCase()}</div>
                                                                <div><b>Location:</b> {violation.transLocation}</div>
                                                                <div><b>Transaction #:</b> {violation.transRecordId}</div> 
                                                                {violation.plateReadFullImage ? 
                                                                    <PlateImageLink onClick={() => showPlateReadImage(key, index)}>
                                                                        ( vehicle image&nbsp;<Icon icon='camera' /> )
                                                                    </PlateImageLink>
                                                                    : ''
                                                                }
                                                            </CardInset>
                                                            )
                                                    })}                                    
                                                </p>                           
                                                <Row>
                                                    {!hideAddVehicle ? <Button onClick={() => addAdditionalVehicle(key)}>Add This Vehicle</Button> : ''}
                                                    <Button onClick={() => payforVendedWash(key)}>Pay for Wash{items.length > 1 ? 'es' : ''}</Button>  
                                                </Row>
                                            </GroupCard>
                                        );                          
                                    default:
                                        return (
                                            <>
                                                Default message of some unkown reason for suspension. Visit our <HamLink href={routes.support.route}><u>Support Page</u></HamLink>. 
                                            </>
                                        )                
                                }
                            })}
                        </Items>
                    </ItemsWrapper>
                </div>                
                <p>                   
                    If you believe you received this message in error or have additional questions, 
                    please visit our <HamLink href={routes.support.route}><u>Support Page</u></HamLink>.           
                </p>                 
                <p>
                    Alternatively, you may chose to leave the club and cancel your account.   
                </p>
                <LeaveButton onClick={cancelPlanWcRestricted}><b>Leave Club</b></LeaveButton>     
            </Card>
        )       
    }

    const WcSuspendedChild = () => {
        return (         
            <Card title='Wash Club Locked' icon='lock'>
                <b>Club Use Restricted!</b>
                <p>
                    The parent account for your club access has been locked due to a Terms of Service violation.
                </p>        
            </Card>           
        )
    }


    if (showingPlans)
        return <ForceLocation route='washClub'><ItemList items={plans} /></ForceLocation>
    else if (wcSuspended && wcAccountType === 'child')
        return <WcSuspendedChild/>
    else if (wcSuspended)       
        return <WcSuspended />
    else if (forceVehicleUpdate)
        return <UpdateVehicle />
    else
        return (            
            <>               
                <WashClub generate={true} />
                <WcDefaultRender
                washClubStatus={washClubStatus}
                onCancel={cancelPlan}
                onReactivate={reactivatePlan}
                togglePlans={() => setShowingPlans(!showingPlans)}
                />
            </>
        )
}

export default WashClubs;

const WcDefaultRender = ({ washClubStatus, onCancel, onReactivate, togglePlans }) => (
  <Card>
    <Button onClick={togglePlans}>View Clubs</Button>
    {washClubStatus === 'endbillingpending' && (
      <Button onClick={onReactivate}>Reactivate Plan</Button>
    )}
    {(washClubStatus === 'active' || washClubStatus === 'endbillingpending') && (
      <Button onClick={onCancel}>Cancel Plan</Button>
    )}
  </Card>
);

export const AddVehicle = () => {
    const {
        state: {
            site_config,
            active_user,
        },
        getVehicle
    } = useGlobalState();

    const addVehicle = () => {
        Modal.Show({
            title: 'Vehicle Info',
            body: (
                <Form
                    inputs={{
                        make: active_user.vehicle?.make || '',
                        model: active_user.vehicle?.model || '',
                        year: active_user.vehicle?.year || '',
                        license: active_user.vehicle?.license || '',
                        color: active_user.vehicle?.color || '',
                        state: active_user.vehicle?.state || '',
                    }}
                    onSubmit={e =>
                        Modal.Promise({
                            title: 'Saving Vehicle',
                            body: <Loading />,
                            callback: HamQuest.post(QuestRoute.user.vehicle, e)
                        })
                            .then(() => {
                                Modal.Close();
                                getVehicle();
                            })
                    }
                >
                    <Select
                        label='State'
                        name='state'
                        required
                        options={
                            [
                                {
                                    value: 0,
                                    label: '-'
                                },
                                ...site_config.states
                            ]
                        }
                    />
                    <Input
                        name='make'
                        label='Make'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='model'
                        label='Model'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='color'
                        label='Color'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='year'
                        label='Year'
                        sanitize={Regex.match.notNumbers}
                        numpad
                        maxLength={4}
                        required
                    />
                    <Input
                        name='license'
                        label='License Plate'
                        required
                        maxLength={10}
                        sanitize={Regex.match.specialChar}
                        validate={/(\w|\d)+/g}
                        mask={(e) => e.toUpperCase()}
                        tag='Used for License Plate Reading'
                    />
                </Form>
            )
        })
    }

    return (
        <Row>
            <Title icon='car'>Hold On!</Title>
            <Span>Your Vehicle Information is required for a wash club plan.</Span>
            <Button onClick={addVehicle}>Add Vehicle Info</Button>
        </Row>
    )
}